import { useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { documentsApi } from 'domain/documents/api';
import type { SearchFavorite } from 'domain/documents/schemas';
import type { EndpointResponse } from 'services/api';
import { queryOptions, useApiMutation } from 'services/query';
import { handleErrorResponseWithToast } from 'lib/toasts';

import { documentsKeys } from './keys';

// Raw cache shape ({ items }) — the read response before `select` sorts it.
export type SearchFavoritesCache = EndpointResponse<typeof documentsApi.getSearchFavorites>;

// Sidebar FavoritesMenu source. companyId is in the key for per-company cache isolation (the
// CompanyId header carries scope to the backend). `select` mirrors the legacy favoritesListSelector
// ordering: saved searches first, then alphabetical by label.
export const searchFavoritesQuery = (companyId: string) =>
  queryOptions({
    queryKey: documentsKeys.searchFavorites(companyId),
    queryFn: () => documentsApi.getSearchFavorites(),
    select: (data): SearchFavorite[] =>
      [...data.items].sort((a, b) => {
        if (a.type === 'saved' && b.type !== 'saved') return -1;
        if (a.type !== 'saved' && b.type === 'saved') return 1;
        return a.label.localeCompare(b.label);
      }),
  });

export const useSearchFavorites = (companyId: string) =>
  useQuery({ ...searchFavoritesQuery(companyId), enabled: Boolean(companyId) });

// Save a search criterion. Thin pass-through — the caller builds the endpoint body
// ({ label, filter, query }); on success we invalidate the list (matching the legacy
// ensureSaveSearchCriteria refetch). No global onError: the WorkspaceSearch save modal owns the
// error UI (inline 406/409 handling), like useCreateBankFeedConnectionMutation.
export const useSaveSearchFavoriteMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: documentsApi.saveSearchCriteria,
    onSuccess: () => qc.invalidateQueries({ queryKey: documentsKeys.searchFavorites(companyId) }),
  });
};

// Delete a saved search by ID. Drops the row from the cache by the ID we already hold (the response
// is not consumed) — mirrors the legacy deleteFavoritesAction.success reducer.
export const useDeleteSearchFavoriteMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: documentsApi.deleteSearchCriteria,
    onSuccess: (_, { body }) => {
      qc.setQueryData<SearchFavoritesCache>(
        documentsKeys.searchFavorites(companyId),
        produce((draft) => {
          if (!draft) return;
          draft.items = draft.items.filter((fav) => fav.ID !== body.ID);
        }),
      );
    },
    onError: handleErrorResponseWithToast,
  });
};
